import React, { useRef, useEffect } from 'react';

// Lightweight scroll-progress bar — pure JS/CSS, no motion/react dependency.
export const ScrollProgress = () => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let raf = 0;
    const update = () => {
      const el = ref.current;
      if (!el) return;
      const scrollTop = window.scrollY;
      const max = document.documentElement.scrollHeight - window.innerHeight;
      const progress = max > 0 ? Math.min(1, Math.max(0, scrollTop / max)) : 0;
      el.style.transform = `scaleX(${progress})`;
    };
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        update();
      });
    };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <div
      ref={ref}
      aria-hidden="true"
      className="fixed top-0 left-0 right-0 h-[2px] z-[100] origin-left pointer-events-none"
      style={{
        background: 'var(--color-accent)',
        transform: 'scaleX(0)',
        willChange: 'transform',
      }}
    />
  );
};

// Plain div wrappers — previously motion.div elements that faded in on mount.
// Removing the opacity animation lets content paint on the first frame and
// lets Lighthouse recognise the hero H1 as the LCP element immediately.
export const RevealSection = ({
  children,
  className = '',
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <div className={className} data-reveal="true">
    {children}
  </div>
);

export const TextReveal = ({
  children,
  className = '',
  as: Component = 'h1',
}: {
  children: React.ReactNode;
  className?: string;
  as?: any;
}) => (
  <Component className={className} data-reveal="true">
    {children}
  </Component>
);

export const RevealCard = ({
  children,
  className = '',
}: {
  children: React.ReactNode;
  className?: string;
  key?: any;
}) => (
  <div className={className} data-reveal="true">
    {children}
  </div>
);

// Kept as no-op exports for API compatibility with pages that still import them.
export const revealVariants = { initial: {}, whileInView: {} };
export const fadeIn = { initial: {}, whileInView: {}, viewport: {}, transition: {} };
export const staggerContainer = { initial: {}, whileInView: {} };
